/**
 * FileTab — All "File" tab features for the Bonsai Mail Outlook Add-in
 *
 * Features ported from vanilla JS:
 *   - Filing status badge
 *   - AI filing suggestions (07_ai_suggest.js)
 *   - File Like Last (18_file_like_last.js)
 *   - Favorites (08_favorites.js)
 *   - Matter search + subfolder navigation (11_matter_search.js, 14_filing.js)
 *   - Selected folder display + File button
 *   - Include-attachments checkbox
 *   - Drop zone (20_drop_zone.js)
 *   - Filing result + conversation filing prompt (15_conversation.js)
 *   - Filing history / recently filed (16_filing_history.js)
 *   - Morning digest
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import {
  Star, Zap, FolderOpen, Search, ChevronRight, ChevronDown,
  Check, X, Clock, Upload, FileText, AlertCircle, AlertTriangle, Loader2,
  Sparkles, ArrowLeft, Trash2, ExternalLink, ChevronUp, RotateCcw,
  Sun, Folder, Mail, Paperclip, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import { errorMessage } from '../../../utils/errorMessage';
import AttachmentSavePrompt from '../../../components/AttachmentSavePrompt';
import AdditionalDestinationsPicker from '../../../components/AdditionalDestinationsPicker';
import AdditionalCopiesConfirmation, { summariseAdditionalCopies } from '../../../components/AdditionalCopiesConfirmation';

const esc = (s) => String(s || '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));

/** Resolve the Attendances subfolder — always targets Attendances, ignores standalone Correspondence */
function findAttendancesFolder(subs) {
  const n = s => (s.name || '').toLowerCase();
  return subs.find(s => n(s).includes('attendance') && n(s).includes('correspondence'))
    || subs.find(s => n(s).includes('attendances'))
    || subs.find(s => n(s).includes('attendance'))
    || subs[0];
}

function findDocumentsFolder(subs) {
  const n = s => (s.name || '').toLowerCase();
  return subs.find(s => n(s) === 'documents')
    || subs.find(s => n(s).includes('document'))
    || subs.find(s => n(s) === 'drafts');
}

export default function FileTab({
  userId,
  currentEmail,
  bonsaiApi,
  autoDeleteAfterFiling,
  timeRecordingEnabled,
  onOpenTimePanel,
  selectedFolder,
  setSelectedFolder,
  emailFilingStatus,
  onFilingComplete,
  onRegisterFileHere,
}) {
  // === STATE ===
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [fileLikeLastDest, setFileLikeLastDest] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  // BM-H130 -- three fee earners reported "search failed" with no actionable
  // detail. The old code silently set results to []. We now hold the last
  // error so we can render a real banner with the reason + a Retry button.
  const [searchError, setSearchError] = useState(null);
  const [subfolders, setSubfolders] = useState([]);
  const [subfolderStack, setSubfolderStack] = useState([]);
  const [loadingSubfolders, setLoadingSubfolders] = useState(false);
  const [includeAttachments, setIncludeAttachments] = useState(true);
  const [filingInProgress, setFilingInProgress] = useState(false);
  const [filingResult, setFilingResult] = useState(null);
  const [recentFilings, setRecentFilings] = useState([]);
  const [recentFilingsExpanded, setRecentFilingsExpanded] = useState(false);
  const [morningDigest, setMorningDigest] = useState(null);
  const [conversationBatch, setConversationBatch] = useState(null);
  const [fileLikeLastLoading, setFileLikeLastLoading] = useState(false);
  const [dropActive, setDropActive] = useState(false);
  const [attachmentFolderId, setAttachmentFolderId] = useState(null);
  const [attachmentFolderName, setAttachmentFolderName] = useState('');

  // Mail PR B — per-email attachment-save prompt state.
  const [attachmentPromptOpen, setAttachmentPromptOpen] = useState(false);
  const [attachmentPromptCtx, setAttachmentPromptCtx] = useState(null);
  const [promptFolders, setPromptFolders] = useState([]);
  const [promptFoldersLoading, setPromptFoldersLoading] = useState(false);
  const [promptFoldersError, setPromptFoldersError] = useState(null);

  // Mail PR A FE — additional destinations picker state.  Multi-select
  // matter top-level folders to copy the filed email into AFTER the
  // canonical Attendances save.  Zero selections is a valid "no copies"
  // outcome.  Sticky across the email-id lifecycle but reset whenever
  // the selected folder/matter changes (handled via effect below).
  const [additionalDestinations, setAdditionalDestinations] = useState([]);
  const [destPickerOpen, setDestPickerOpen] = useState(false);

  // PR #462 — auto-file countdown removed. Filing is now explicit
  // click-to-file only. AI suggestion chips below still render.
  const searchTimerRef = useRef(null);
  const suggestionCacheRef = useRef({ emailId: null, result: null });
  const pollRef = useRef(null);
  const loadSubfoldersIdRef = useRef(0);
  const userClearedRef = useRef(false);

  // === EFFECTS ===
  useEffect(() => {
    if (!userId) return;
    loadFavorites();
    loadFileLikeLast();
    loadRecentFilings();
    loadMorningDigest();
  }, [userId]);

  useEffect(() => {
    if (!userId || !currentEmail?.id) return;
    getAISuggestion();
    setFilingResult(null);
    setConversationBatch(null);
    // Mail PR A FE — reset additional destinations whenever the user
    // navigates to a different email; previous email's selection MUST
    // NOT leak across.
    setAdditionalDestinations([]);
  }, [userId, currentEmail?.id]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // === AI SUGGESTIONS ===
  const getAISuggestion = useCallback(async () => {
    if (!currentEmail?.id) return;
    if (suggestionCacheRef.current.emailId === currentEmail.id && suggestionCacheRef.current.result) {
      setAiSuggestions(suggestionCacheRef.current.result.suggestions || []);
      return;
    }
    setLoadingSuggestions(true);
    try {
      const resp = await bonsaiApi.post('/sharepoint/suggest-filing', {
        subject: currentEmail.subject,
        from_address: currentEmail.from,
        body_preview: currentEmail.body,
        ai_extracted_reference: currentEmail.parsedMatterRef || '',
      });
      const suggestions = resp.data?.suggestions || [];
      suggestionCacheRef.current = { emailId: currentEmail.id, result: resp.data };
      setAiSuggestions(suggestions);
    } catch { setAiSuggestions([]); }
    setLoadingSuggestions(false);
  }, [bonsaiApi, currentEmail]);

  const acceptSuggestion = useCallback((suggestion) => {
    bonsaiApi.post('/filing/log-suggestion-choice', {
      chosen_reference: suggestion.matter_reference,
      suggestions_shown: aiSuggestions.length,
      chosen_index: aiSuggestions.indexOf(suggestion),
    }).catch(() => {});

    if (suggestion.drive_id && suggestion.folder_id) {
      setSelectedFolder({
        type: 'suggestion',
        driveId: suggestion.drive_id,
        itemId: suggestion.folder_id,
        name: suggestion.matter_reference || suggestion.client_name || 'Suggestion',
        reference: suggestion.matter_reference,
        webUrl: suggestion.web_url || '',
      });
      loadSubfolders(suggestion.drive_id, suggestion.folder_id, suggestion.matter_reference);
    } else if (suggestion.sharepoint_path) {
      setSelectedFolder({
        type: 'browse',
        sharePointUrl: suggestion.sharepoint_path,
        folderPath: suggestion.sharepoint_path,
        reference: suggestion.matter_reference,
        name: suggestion.matter_reference || suggestion.client_name || '',
      });
    }
  }, [aiSuggestions, bonsaiApi, setSelectedFolder]);

  const [undoingFiling, setUndoingFiling] = useState(false);

  // Undo any filing — works for both auto-filed (filingResult) and
  // already-filed emails surfaced via emailFilingStatus from parent.
  const undoFiling = useCallback(async (filingId) => {
    if (!filingId) return;
    setUndoingFiling(true);
    try {
      const resp = await bonsaiApi.post(`/filing/undo/${filingId}`);
      if (resp.data?.status === 'ok') {
        setFilingResult(null);
        toast.success('Filing undone — SharePoint copy deleted');
        if (onFilingComplete) onFilingComplete();
      } else {
        toast.error(resp.data?.message || 'Undo failed');
      }
    } catch (e) {
      toast.error(errorMessage(e, 'Undo failed'));
    }
    setUndoingFiling(false);
  }, [bonsaiApi, onFilingComplete]);


  // === FAVORITES ===
  const loadFavorites = useCallback(async () => {
    try {
      const resp = await bonsaiApi.get('/sharepoint/favorites');
      setFavorites(resp.data?.items || []);
    } catch { setFavorites([]); }
  }, [bonsaiApi]);

  const addToFavorites = useCallback(async () => {
    if (!selectedFolder) return;
    try {
      await bonsaiApi.post('/sharepoint/favorites', {
        name: selectedFolder.name || selectedFolder.reference,
        path: selectedFolder.folderPath || selectedFolder.sharePointUrl,
        driveId: selectedFolder.driveId,
        itemId: selectedFolder.itemId,
        webUrl: selectedFolder.webUrl,
        type: selectedFolder.type,
      });
      loadFavorites();
      toast.success('Added to favorites');
    } catch { toast.error('Failed to add favorite'); }
  }, [bonsaiApi, selectedFolder, loadFavorites]);

  const removeFavorite = useCallback(async (id) => {
    try {
      await bonsaiApi.delete(`/sharepoint/favorites/${id}`);
      loadFavorites();
    } catch { toast.error('Failed to remove favorite'); }
  }, [bonsaiApi, loadFavorites]);

  const selectFavorite = useCallback((fav) => {
    setSelectedFolder({ ...fav, type: fav.type || 'favorite' });
    if (fav.driveId || fav.drive_id) {
      const driveId = fav.driveId || fav.drive_id;
      const itemId = fav.itemId || fav.folder_id || fav.item_id || 'root';
      setSubfolderStack([]);
      loadSubfolders(driveId, itemId, fav.name);
    }
  }, [setSelectedFolder]);

  const quickFileFavorite = useCallback(async (fav) => {
    if (!currentEmail?.id) { toast.error('No email selected'); return; }
    setFilingInProgress(true);
    try {
      const driveId = fav.driveId || fav.drive_id;
      const itemId = fav.itemId || fav.folder_id || fav.item_id;
      if (driveId && itemId) {
        const subResp = await bonsaiApi.get(`/mail/matters/${driveId}/${itemId}/subfolders`);
        const subs = subResp.data?.subfolders || [];
        if (subs.length > 0) {
          const emailFolder = findAttendancesFolder(subs);
          if (emailFolder) {
            // PR #466 — quick-file from a favourites/star button click.
            const body = { email_id: currentEmail.id, user_intent: 'fav', drive_id: driveId, folder_id: emailFolder.id, include_attachments: includeAttachments };
            const docsFolder = findDocumentsFolder(subs);
            if (includeAttachments && docsFolder) {
              body.attachment_drive_id = driveId;
              body.attachment_folder_id = docsFolder.id;
            }
            const resp = await bonsaiApi.post('/filing/file', body);
            if (resp.data?.success) {
              setFilingResult({ success: true, message: `Quick filed to ${fav.name} / ${emailFolder.name}!`, url: resp.data.sharepoint_url });
              handlePostFiling();
              return;
            }
          }
        }
      }
      // PR #466 — fav-click fallback (no subfolders / fallback URL path).
      const body = { email_id: currentEmail.id, user_intent: 'fav', include_attachments: includeAttachments };
      if (fav.driveId && fav.itemId) {
        body.drive_id = fav.driveId;
        body.folder_id = fav.itemId;
      } else if (fav.webUrl || fav.path) {
        body.sharepoint_path = fav.webUrl || fav.path;
      }
      const resp = await bonsaiApi.post('/filing/file', body);
      if (resp.data?.success) {
        setFilingResult({ success: true, message: `Quick filed to ${fav.name}!`, url: resp.data.sharepoint_url });
        handlePostFiling();
      } else {
        setFilingResult({ success: false, message: resp.data?.message || 'Filing failed' });
      }
    } catch (e) {
      setFilingResult({ success: false, message: errorMessage(e, 'Filing failed') });
      setFilingInProgress(false);
    }
  }, [bonsaiApi, currentEmail, includeAttachments]);

  // === FILE LIKE LAST ===
  const loadFileLikeLast = useCallback(async () => {
    try {
      const resp = await bonsaiApi.get('/filing/recent-destinations?limit=1');
      const dests = resp.data?.destinations || [];
      const dest = dests.length > 0 ? dests[0] : null;
      if (dest?.matter_reference?.includes('.NEH.')) {
        setFileLikeLastDest(null);
        return;
      }
      setFileLikeLastDest(dest);
    } catch { setFileLikeLastDest(null); }
  }, [bonsaiApi]);

  const fileLikeLast = useCallback(async () => {
    if (!fileLikeLastDest || !currentEmail?.id) return;
    setFileLikeLastLoading(true);
    try {
      const dest = fileLikeLastDest;
      const driveId = dest.driveId || dest.drive_id || '';
      const folderId = dest.itemId || dest.folder_id || '';
      if (driveId && folderId) {
        const subResp = await bonsaiApi.get(`/mail/matters/${driveId}/${folderId}/subfolders`);
        const subs = subResp.data?.subfolders || [];
        if (subs.length > 0) {
          const emailFolder = findAttendancesFolder(subs);
          const docsFolder = findDocumentsFolder(subs);
          if (emailFolder) {
            const body = {
              email_id: currentEmail.id,
              // PR #466 — "File like last" path; quick-refile to the most
              // recent destination is a quick_refile gesture.
              user_intent: 'quick_refile',
              drive_id: driveId,
              folder_id: emailFolder.id,
              include_attachments: includeAttachments && currentEmail.hasAttachments,
            };
            if (includeAttachments && docsFolder) {
              body.attachment_drive_id = driveId;
              body.attachment_folder_id = docsFolder.id;
            }
            const resp = await bonsaiApi.post('/filing/file', body);
            if (resp.data?.success) {
              const destName = dest.folder_name || dest.matter_reference || '';
              setFilingResult({ success: true, message: `Filed to ${destName} / ${emailFolder.name}!`, url: resp.data.sharepoint_url });
              handlePostFiling();
              setFileLikeLastLoading(false);
              return;
            }
          }
        } else {
          // PR #466 — same "File like last" path; no-subfolders branch.
          const body = { email_id: currentEmail.id, user_intent: 'quick_refile', drive_id: driveId, folder_id: folderId, include_attachments: includeAttachments };
          const resp = await bonsaiApi.post('/filing/file', body);
          if (resp.data?.success) {
            setFilingResult({ success: true, message: `Filed to ${dest.folder_name || dest.matter_reference || 'Last folder'}!` });
            handlePostFiling();
            setFileLikeLastLoading(false);
            return;
          }
        }
      }
      setSelectedFolder({
        type: 'recent', driveId, itemId: folderId,
        name: dest.folder_name || dest.matter_reference || 'Last folder',
        sharePointUrl: dest.sharepoint_url || '',
      });
    } catch (e) {
      setFilingResult({ success: false, message: errorMessage(e, 'File Like Last failed') });
    }
    setFileLikeLastLoading(false);
  }, [bonsaiApi, fileLikeLastDest, currentEmail, includeAttachments, setSelectedFolder]);

  // === MATTER SEARCH ===
  // BM-H130 -- Rebekah/Emily/Jodie reported (10 Aug) that matter search
  // "just says search failed" for names, addresses, references. Root cause:
  // the old handler swallowed every error and set results to []. Users had
  // no way to tell a genuine empty result from a 401 (re-auth needed) from
  // a 5xx (backend down) from a network drop. We now classify + surface it.
  const classifySearchError = (e) => {
    if (!e?.response) return { kind: 'network', message: 'No response from server. Check your connection and try again.' };
    const status = e.response.status;
    if (status === 401 || status === 403) return { kind: 'auth', message: 'Your session has expired. Please sign in again to Bonsai Mail.' };
    if (status >= 500) return { kind: 'server', message: `Server error (${status}). Please retry in a moment.` };
    return { kind: 'other', message: `Search failed (${status}). ${e.response?.data?.detail || 'Please try again.'}` };
  };

  const searchMatters = useCallback(async (query) => {
    if (query.length < 1) { setSearchResults([]); setSearchError(null); return; }
    setSearching(true);
    setSearchError(null);
    try {
      const resp = await bonsaiApi.get(`/filing/matters?q=${encodeURIComponent(query)}&limit=10`);
      setSearchResults(resp.data || []);
    } catch (e) {
      setSearchResults([]);
      setSearchError(classifySearchError(e));
    }
    setSearching(false);
  }, [bonsaiApi]);

  const handleSearchChange = useCallback((e) => {
    const q = e.target.value;
    setSearchQuery(q);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => searchMatters(q), 300);
  }, [searchMatters]);

  const selectMatter = useCallback((matter) => {
    loadSubfoldersIdRef.current++;
    if (searchTimerRef.current) { clearTimeout(searchTimerRef.current); searchTimerRef.current = null; }
    const ref = matter.reference || matter.matter_title || matter.title || '';
    const name = matter.matter_title || matter.title || matter.reference || matter.client_name || '';
    setSelectedFolder({
      type: 'matter',
      matterId: matter.id,
      sharePointUrl: matter.sharepoint_url || '',
      name: name,
      reference: ref,
      driveId: matter.drive_id || '',
      itemId: matter.folder_id || '',
      webUrl: matter.web_url || matter.sharepoint_url || '',
    });
    setSearchQuery('');
    setSearchResults([]);
    if (matter.drive_id && matter.folder_id) {
      setSubfolderStack([]);
      loadSubfolders(matter.drive_id, matter.folder_id, name);
    }
  }, [setSelectedFolder, loadSubfolders]);

  // === SUBFOLDER NAVIGATION ===
  const loadSubfolders = useCallback(async (driveId, folderId, label) => {
    const thisId = ++loadSubfoldersIdRef.current;
    setLoadingSubfolders(true);
    try {
      const resp = await bonsaiApi.get(`/mail/matters/${driveId}/${folderId}/subfolders`);
      if (thisId !== loadSubfoldersIdRef.current) return;
      const subs = resp.data?.subfolders || [];
      setSubfolders(subs);
      const attendances = findAttendancesFolder(subs);
      if (attendances) {
        setSelectedFolder(prev => {
          if (thisId !== loadSubfoldersIdRef.current) return prev;
          return prev ? {
            ...prev,
            itemId: attendances.id,
            name: `${prev.reference || prev.matterId || label || ''} / ${attendances.name}`,
            _parentFolderId: folderId,
          } : prev;
        });
        setAttachmentFolderId(attendances.id);
        setAttachmentFolderName(attendances.name);
      }
    } catch { if (thisId === loadSubfoldersIdRef.current) setSubfolders([]); }
    if (thisId === loadSubfoldersIdRef.current) setLoadingSubfolders(false);
  }, [bonsaiApi, setSelectedFolder]);

  const selectSubfolder = useCallback((sub) => {
    setSubfolderStack(prev => [...prev, { ...selectedFolder, subfolders }]);
    setSelectedFolder({
      type: 'subfolder',
      driveId: sub.driveId || selectedFolder?.driveId,
      itemId: sub.id,
      name: sub.name,
      reference: selectedFolder?.reference,
      webUrl: sub.webUrl || '',
    });
    loadSubfolders(sub.driveId || selectedFolder?.driveId, sub.id, sub.name);
  }, [selectedFolder, subfolders, setSelectedFolder, loadSubfolders]);

  const goBackSubfolder = useCallback(() => {
    if (subfolderStack.length === 0) return;
    const prev = subfolderStack[subfolderStack.length - 1];
    setSubfolderStack(s => s.slice(0, -1));
    setSelectedFolder(prev);
    setSubfolders(prev.subfolders || []);
  }, [subfolderStack, setSelectedFolder]);

  // === Mail PR B — attachment-save prompt helpers ===
  const askAttachmentDecision = useCallback(({ attachmentCount, folderDriveId, folderItemId }) => {
    return new Promise((resolve) => {
      setPromptFolders([]);
      setPromptFoldersError(null);
      setAttachmentPromptCtx({
        attachmentCount,
        folderDriveId,
        folderItemId,
        resolve,
      });
      setAttachmentPromptOpen(true);
    });
  }, []);

  const loadPromptFolders = useCallback(async (driveId, itemId) => {
    if (!driveId || !itemId) {
      setPromptFolders([]);
      setPromptFoldersError(new Error('No matter folder configured.'));
      return;
    }
    setPromptFoldersLoading(true);
    setPromptFoldersError(null);
    try {
      const resp = await bonsaiApi.get(`/mail/matters/${driveId}/${itemId}/subfolders`);
      setPromptFolders(resp.data?.subfolders || []);
    } catch (e) {
      setPromptFoldersError(e);
      setPromptFolders([]);
    } finally {
      setPromptFoldersLoading(false);
    }
  }, [bonsaiApi]);

  // === FILING ===
  const handleFile = useCallback(async () => {
    if (!currentEmail?.id || !selectedFolder) return;
    setFilingInProgress(true);
    setFilingResult(null);
    try {
      const body = {
        email_id: currentEmail.id,
        // PR #466 — main "File" button click in FileTab (React Outlook
        // panel). click_file regardless of how selectedFolder was
        // assembled.
        user_intent: 'click_file',
        include_attachments: includeAttachments && currentEmail.hasAttachments,
        // Mail PR A FE — additional copy destinations selected via the
        // picker.  Always sent (empty list when none picked) so the
        // backend response always carries the matching additional_copies
        // shape.
        additional_destinations: additionalDestinations,
      };
      if (selectedFolder.driveId && selectedFolder.itemId) {
        body.drive_id = selectedFolder.driveId;
        body.folder_id = selectedFolder.itemId;
      } else if (selectedFolder.sharePointUrl || selectedFolder.webUrl) {
        body.sharepoint_path = selectedFolder.sharePointUrl || selectedFolder.webUrl;
      }
      if (selectedFolder.matterId) body.matter_id = selectedFolder.matterId;
      if (selectedFolder.reference) body.matter_reference = selectedFolder.reference;
      if (includeAttachments && attachmentFolderId && attachmentFolderId !== body.folder_id) {
        body.attachment_folder_id = attachmentFolderId;
      }

      // Mail PR B — per-email attachment-save prompt.  Skip the prompt
      // entirely when the email has no attachments (case 6).
      const attCount = currentEmail.hasAttachments
        ? (currentEmail.attachmentCount || currentEmail.attachments_count || 1)
        : 0;
      if (attCount > 0 && includeAttachments) {
        const decision = await askAttachmentDecision({
          attachmentCount: attCount,
          folderDriveId: selectedFolder.driveId,
          folderItemId: selectedFolder.itemId,
        });
        body.include_attachments = decision.include_attachments;
        if (decision.attachment_folder_id) {
          body.attachment_folder_id = decision.attachment_folder_id;
        }
        body.attachment_decision = decision.attachment_decision;
      } else {
        body.include_attachments = false;
        body.attachment_decision = 'skipped';
      }

      const resp = await bonsaiApi.post('/filing/file', body);
      if (resp.data?.success) {
        window._lastFiledDriveId = body.drive_id || '';
        window._lastFiledFolderId = body.folder_id || '';
        // Mail PR A FE — surface per-folder copy outcomes.  If ANY copy
        // failed, treat the overall result as a "mixed" outcome rather
        // than green-tick success (Vanessa's standing rule).
        const additionalCopies = resp.data.additional_copies || [];
        const summary = summariseAdditionalCopies(additionalCopies);
        let resultMsg = `Filed to ${(selectedFolder.name || selectedFolder.reference || 'SharePoint').split(' → ').pop().trim()}!`;
        if (summary.total > 0) {
          resultMsg += ` ${summary.copied}/${summary.total} extra ${summary.total === 1 ? 'copy' : 'copies'}`;
          if (summary.inProgress > 0) resultMsg += `, ${summary.inProgress} still copying`;
          if (summary.failed > 0) resultMsg += `, ${summary.failed} failed`;
        }
        setFilingResult({
          success: !summary.anyFailed,
          mixed: summary.anyFailed || summary.anyInProgress,
          message: resultMsg,
          url: resp.data.sharepoint_url,
          filingId: resp.data.filing_id,
          additionalCopies,
        });
        handlePostFiling();
      } else {
        setFilingResult({ success: false, message: resp.data?.detail || resp.data?.message || 'Filing failed' });
      }
    } catch (e) {
      setFilingResult({ success: false, message: errorMessage(e, 'Filing failed') });
    }
    setFilingInProgress(false);
  }, [bonsaiApi, currentEmail, selectedFolder, includeAttachments]);

  const handlePostFiling = useCallback(() => {
    loadRecentFilings();
    loadFileLikeLast();
    if (onFilingComplete) onFilingComplete();
    if (autoDeleteAfterFiling && currentEmail?.id) {
      autoDeleteEmail(currentEmail.id);
    }
  }, [autoDeleteAfterFiling, currentEmail?.id, onFilingComplete]);

  // === CONVERSATION FILING ===
  const fileConversation = useCallback(async () => {
    if (!currentEmail?.conversationId) {
      handleFile();
      return;
    }
    try {
      const body = {
        conversation_id: currentEmail.conversationId,
        // PR #466 — addin FileTab "File conversation" button click.
        user_intent: 'click_file',
        subject: currentEmail.subject || '',
        include_attachments: includeAttachments,
      };
      const usedDriveId = window._lastFiledDriveId || selectedFolder?.driveId || '';
      const usedFolderId = window._lastFiledFolderId || selectedFolder?.itemId || '';
      if (selectedFolder?.sharePointUrl || selectedFolder?.webUrl) body.sharepoint_path = selectedFolder.sharePointUrl || selectedFolder.webUrl;
      if (selectedFolder?.matterId) body.matter_id = selectedFolder.matterId;
      if (selectedFolder?.reference) body.matter_reference = selectedFolder.reference;
      if (usedDriveId) body.drive_id = usedDriveId;
      if (usedFolderId) body.folder_id = usedFolderId;

      setConversationBatch({ id: null, total: '...', completed: 0, failed: 0, status: 'processing' });
      const resp = await bonsaiApi.post('/filing/conversation', body);
      if (resp.data?.batch_id) {
        setConversationBatch({ id: resp.data.batch_id, total: resp.data.total, completed: 0, failed: 0, status: 'processing' });
        pollConversationProgress(resp.data.batch_id, resp.data.total);
      }
    } catch (e) {
      setFilingResult({ success: false, message: errorMessage(e, 'Conversation filing failed') });
    }
  }, [bonsaiApi, currentEmail, selectedFolder, includeAttachments]);

  const pollConversationProgress = useCallback((batchId, total) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const resp = await bonsaiApi.get(`/filing/batch/${batchId}`);
        const batch = resp.data?.batch;
        if (!batch) return;
        setConversationBatch({ id: batchId, total, completed: batch.completed || 0, failed: batch.failed || 0, status: batch.status });
        if (batch.status === 'completed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          loadRecentFilings();
        }
      } catch {}
    }, 2000);
  }, [bonsaiApi]);

  // === DELETE / KEEP EMAIL ===
  const deleteFiledEmail = useCallback(async (emailId) => {
    try {
      await bonsaiApi.post('/outlook/move-email', { message_id: emailId, destination_name: 'DeletedItems' });
      toast.success('Email moved to Deleted Items');
    } catch {
      toast.error('Failed to delete email');
    }
  }, [bonsaiApi]);

  const autoDeleteEmail = useCallback(async (emailId) => {
    try {
      await bonsaiApi.post('/outlook/move-email', { message_id: emailId, destination_name: 'DeletedItems' });
    } catch {}
  }, [bonsaiApi]);

  // === FILING HISTORY ===
  const loadRecentFilings = useCallback(async () => {
    try {
      const resp = await bonsaiApi.get('/filing/history?limit=5');
      setRecentFilings(resp.data?.filings || []);
    } catch { setRecentFilings([]); }
  }, [bonsaiApi]);

  // === MORNING DIGEST ===
  const loadMorningDigest = useCallback(async () => {
    try {
      const resp = await bonsaiApi.get('/emails/digest');
      if (resp.data?.total_inbox_7d) setMorningDigest(resp.data);
      else setMorningDigest(null);
    } catch { setMorningDigest(null); }
  }, [bonsaiApi]);

  // === DROP ZONE ===
  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDropActive(false);
    if (!selectedFolder) { toast.error('Select a folder first'); return; }
    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) {
      if (typeof Office !== 'undefined' && Office.context?.mailbox?.getSelectedItemsAsync) {
        Office.context.mailbox.getSelectedItemsAsync(async (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded && result.value?.length > 0) {
            const emailIds = result.value.map(item => item.itemId).filter(Boolean);
            if (!emailIds.length) { handleFile(); return; }
            setFilingInProgress(true);
            try {
              const batchResp = await bonsaiApi.post('/filing/batch', {
                email_ids: emailIds,
                // PR #466 — addin drop-zone receives email items dragged
                // from the Outlook inbox (multi-select dropped onto the
                // FileTab's drop zone). drag_drop is the vocab term.
                user_intent: 'drag_drop',
                drive_id: selectedFolder.driveId || '',
                folder_id: selectedFolder.itemId || '',
                matter_reference: selectedFolder.reference || selectedFolder.name || '',
                include_attachments: includeAttachments,
              });
              const batchId = batchResp.data?.batch_id;
              if (batchId) {
                let done = false;
                while (!done) {
                  await new Promise(r => setTimeout(r, 1500));
                  const statusResp = await bonsaiApi.get(`/filing/batch/${batchId}`);
                  const batch = statusResp.data?.batch || statusResp.data || {};
                  if (batch.status === 'completed' || batch.status === 'complete' || batch.status === 'done') {
                    done = true;
                    const displayName = (selectedFolder.reference || selectedFolder.name || 'SharePoint').split(' → ').pop().trim();
                    setFilingResult({ success: true, message: `${batch.completed || emailIds.length} emails filed to ${displayName}`, url: batch.sharepoint_url });
                    loadRecentFilings();
                  } else if (batch.status === 'failed') {
                    done = true;
                    setFilingResult({ success: false, message: 'Batch filing failed' });
                  }
                }
              } else {
                const displayName = (selectedFolder.reference || selectedFolder.name || 'SharePoint').split(' → ').pop().trim();
                setFilingResult({ success: true, message: `${emailIds.length} emails filed to ${displayName}` });
                loadRecentFilings();
              }
            } catch (err) {
              setFilingResult({ success: false, message: errorMessage(err, 'Batch filing failed') });
            }
            setFilingInProgress(false);
          } else if (currentEmail?.id) {
            handleFile();
          } else {
            toast.error('No emails selected');
          }
        });
      } else if (currentEmail?.id) {
        handleFile();
      } else {
        toast.error('No files to upload');
      }
      return;
    }
    const driveId = selectedFolder.driveId || '';
    const folderId = selectedFolder.itemId || '';
    const matterRef = selectedFolder.reference || selectedFolder.name || '';
    setFilingInProgress(true);
    try {
      const results = await Promise.all(Array.from(files).map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('drive_id', driveId);
        formData.append('folder_id', folderId);
        formData.append('matter_reference', matterRef);
        try {
          await bonsaiApi.post('/filing/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
          return { success: true, name: file.name };
        } catch (err) {
          return { success: false, name: file.name, error: errorMessage(err, 'Attachment filing failed') };
        }
      }));
      const uploaded = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success).length;
      if (failed === 0) {
        setFilingResult({ success: true, message: `${uploaded} file${uploaded > 1 ? 's' : ''} uploaded to ${matterRef}` });
        loadRecentFilings();
      } else {
        setFilingResult({ success: false, message: `${uploaded} uploaded, ${failed} failed` });
      }
    } catch (e) {
      setFilingResult({ success: false, message: errorMessage(e, 'Filing failed') });
    }
    setFilingInProgress(false);
  }, [bonsaiApi, selectedFolder, currentEmail, handleFile, includeAttachments, loadRecentFilings]);

  const clearSelection = useCallback(() => {
    userClearedRef.current = true;
    setSelectedFolder(null);
    setSubfolders([]);
    setSubfolderStack([]);
    setFilingResult(null);
    setConversationBatch(null);
    setAttachmentFolderId(null);
    setAttachmentFolderName('');
  }, [setSelectedFolder]);

  // === BROWSE "FILE HERE" ===
  const handleBrowseFileHere = useCallback(async (folder) => {
    setSelectedFolder({
      type: 'browse',
      driveId: folder.driveId,
      itemId: folder.itemId || folder.id || 'root',
      name: folder.name,
      reference: folder.reference || folder.name,
      webUrl: folder.webUrl || '',
    });

    if (typeof Office !== 'undefined' && Office.context?.mailbox?.getSelectedItemsAsync) {
      Office.context.mailbox.getSelectedItemsAsync(async (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded && result.value?.length > 0) {
          const emailIds = result.value.map(item => item.itemId);
          setFilingInProgress(true);
          const displayName = (folder.reference || folder.name || 'SharePoint').split(' → ').pop().trim();
          try {
            const batchResp = await bonsaiApi.post('/filing/batch', {
              email_ids: emailIds,
              // PR #466 — handleBrowseFileHere fires when the user clicks
              // "File here" on a folder row in the Browse panel.
              user_intent: 'browse_pick',
              drive_id: folder.driveId,
              folder_id: folder.itemId || folder.id || 'root',
              matter_reference: folder.reference || folder.name || '',
              include_attachments: includeAttachments,
            });
            const batchId = batchResp.data?.batch_id;
            if (batchId) {
              let done = false;
              while (!done) {
                await new Promise(r => setTimeout(r, 1500));
                const statusResp = await bonsaiApi.get(`/filing/batch/${batchId}`);
                const s = statusResp.data?.batch || statusResp.data || {};
                if (s.status === 'completed' || s.status === 'complete' || s.status === 'done') {
                  done = true;
                  setFilingResult({ success: true, message: `${s.completed || emailIds.length} emails filed to ${displayName}`, url: s.sharepoint_url });
                  loadRecentFilings();
                } else if (s.status === 'failed') {
                  done = true;
                  setFilingResult({ success: false, message: 'Batch filing failed' });
                }
              }
            } else {
              setFilingResult({ success: true, message: `${emailIds.length} emails filed to ${displayName}` });
              loadRecentFilings();
            }
          } catch (err) {
            setFilingResult({ success: false, message: errorMessage(err, 'Batch filing failed') });
          }
          setFilingInProgress(false);
        } else {
          handleFile();
        }
      });
    } else {
      handleFile();
    }
  }, [bonsaiApi, includeAttachments, handleFile, loadRecentFilings, setSelectedFolder]);

  useEffect(() => {
    onRegisterFileHere?.(handleBrowseFileHere);
  }, [handleBrowseFileHere, onRegisterFileHere]);

  // === RENDER ===
  return (
    <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-2.5" data-testid="file-tab"
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDropActive(true); }}
      onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDropActive(false); }}
      onDrop={handleDrop}>

      {/* FILING STATUS from parent — shown when email already filed before this session */}
      {emailFilingStatus?.filed && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-teal-dim/20 border border-teal/40" data-testid="email-filing-status">
          <CheckCircle2 className="w-4 h-4 text-teal flex-shrink-0" />
          <div className="flex-1">
            <span className="text-xs font-medium text-teal">Already filed</span>
            {emailFilingStatus.matter_ref && (
              <span className="text-xs text-teal/70 ml-1">to {emailFilingStatus.matter_ref}</span>
            )}
            {emailFilingStatus.web_url && (
              <a href={emailFilingStatus.web_url} target="_blank" rel="noreferrer"
                className="text-xs text-blue-400 hover:underline block mt-0.5">
                View in SharePoint
              </a>
            )}
          </div>
          {/* Undo button for previously filed emails (filing_id from parent) */}
          {emailFilingStatus.filing_id && (
            <button
              onClick={() => undoFiling(emailFilingStatus.filing_id)}
              disabled={undoingFiling}
              className="flex items-center gap-1 text-xs text-status-amber hover:text-status-amber bg-status-amber-dim/30 border border-amber-800/40 px-2 py-1 rounded transition-colors disabled:opacity-50"
              data-testid="undo-filing-status-btn"
            >
              {undoingFiling ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
              {undoingFiling ? 'Undoing...' : 'Undo'}
            </button>
          )}
        </div>
      )}

      {/* AUTO-PARSED MATTER REF */}
      {currentEmail?.parsedMatterRef && !emailFilingStatus?.filed && !selectedFolder && (
        <button
          onClick={() => { setSearchQuery(currentEmail.parsedMatterRef); searchMatters(currentEmail.parsedMatterRef); }}
          className="w-full flex items-center gap-2 p-2.5 rounded-lg border border-blue-700/30 bg-blue-900/10 hover:bg-blue-900/20 transition-colors text-left"
          data-testid="auto-parsed-matter-ref">
          <Search className="w-4 h-4 text-blue-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="text-xs font-medium text-blue-300">{currentEmail.parsedMatterRef}</span>
            <span className="text-[10px] text-blue-500 block">Parsed from subject — click to search</span>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-600" />
        </button>
      )}

      {/* DROP ZONE OVERLAY */}
      {dropActive && (
        <div className="absolute inset-0 z-20 bg-teal-dim/30 backdrop-blur-sm border-2 border-dashed border-teal rounded-lg flex items-center justify-center" data-testid="drop-zone-overlay">
          <div className="text-center">
            <Upload className="w-8 h-8 text-teal mx-auto mb-2" />
            <p className="text-sm font-medium text-teal">Drop files to upload</p>
            <p className="text-[10px] text-teal">
              {selectedFolder ? `to ${selectedFolder.name || selectedFolder.reference}` : 'Select a folder first'}
            </p>
          </div>
        </div>
      )}

      {/* AI SUGGESTIONS */}
      {loadingSuggestions && (
        <div className="p-3 rounded-lg border border-blue-700/30 bg-blue-900/10 flex items-center gap-2">
          <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
          <span className="text-xs text-blue-300">Analysing email...</span>
        </div>
      )}
      {!loadingSuggestions && aiSuggestions.length > 0 && (
        <div className="p-3 rounded-lg border bg-blue-900/20 border-blue-700/50" data-testid="ai-suggestions">
          <div className="flex items-center gap-1.5 mb-2">
            <Sparkles className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-bold text-blue-300">Smart Filing Suggestions</span>
          </div>
          <div className="space-y-2">
            {aiSuggestions.map((s, i) => {
              const pct = Math.round((s.confidence || 0) * 100);
              const isTop = i === 0 && pct >= 70;
              return (
                <div key={i} className={`p-2.5 rounded border ${isTop ? 'bg-teal-dim/30 border-teal/40' : 'bg-gray-800/40 border-gray-700/40'}`} data-testid={`smart-suggestion-${i}`}>
                  <div className="text-sm font-semibold text-white">{s.matter_reference || 'Unknown'}</div>
                  {s.client_name && <div className="text-xs text-gray-300 mt-0.5">{s.client_name}</div>}
                  <div className="text-[11px] text-teal mt-0.5">→ Attendances subfolder</div>
                  {s.match_reason && <div className="text-[11px] text-gray-500 mt-0.5">{s.match_reason}</div>}
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-400">{pct}% confidence</span>
                    <Button size="sm" onClick={() => acceptSuggestion(s)}
                      className={`h-7 text-xs ${isTop ? 'bg-teal hover:bg-teal' : 'bg-blue-600 hover:bg-blue-500'}`}
                      data-testid={`file-suggestion-${i}`}>
                      File here
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* FILE LIKE LAST */}
      {fileLikeLastDest && currentEmail && (
        <div className="hidden" id="file-like-last-container" ref={el => { if (el) el.classList.remove('hidden'); }}>
          <button onClick={fileLikeLast} disabled={fileLikeLastLoading}
            className="w-full flex items-center gap-2 p-2.5 rounded-lg border border-teal/30 bg-teal-dim/10 hover:bg-teal-dim/20 transition-colors text-left"
            data-testid="file-like-last-btn">
            <Zap className="w-4 h-4 text-teal flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-xs font-medium text-teal">
                {fileLikeLastLoading ? 'Filing...' : 'File Like Last'}
              </span>
              <span className="text-[11px] text-gray-400 block truncate" data-testid="file-like-last-dest">
                {fileLikeLastDest.folder_name || fileLikeLastDest.matter_reference || 'Last folder'}
              </span>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-600" />
          </button>
        </div>
      )}

      {/* FAVORITES */}
      {favorites.length > 0 && (
        <div data-testid="favorites-section">
          <div className="text-xs font-semibold text-status-amber/80 mb-1.5 flex items-center gap-1">
            <Star className="w-3 h-3" /> Favorites
          </div>
          <div className="space-y-1">
            {favorites.map(f => (
              <div key={f.id} className="flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-800/40 transition-colors group" onClick={() => selectFavorite(f)}>
                <Star className="w-3.5 h-3.5 text-status-amber fill-amber-500" />
                <span className="text-sm text-gray-300 truncate flex-1">{f.name}</span>
                <button onClick={(e) => { e.stopPropagation(); quickFileFavorite(f); }}
                  className="text-status-green hover:text-status-green p-1 opacity-60 group-hover:opacity-100" title="Quick file">
                  <Zap className="w-3.5 h-3.5" />
                </button>
                <button onClick={(e) => { e.stopPropagation(); removeFavorite(f.id); }}
                  className="text-gray-500 hover:text-status-red p-1 opacity-60 group-hover:opacity-100" title="Remove">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MATTER SEARCH */}
      <div data-testid="matter-search-section">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search matters..."
            className="pl-9 h-9 bg-gray-800/60 border-gray-700 text-sm text-white placeholder:text-gray-500"
            data-testid="matter-search-input"
          />
          {searching && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400 animate-spin" />}
        </div>
        {/* BM-H130 -- surface the actual failure instead of silently returning []. */}
        {searchError && !searching && (
          <div
            className="mt-1 border border-status-red/40 bg-status-red/10 rounded-lg px-3 py-2"
            data-testid="matter-search-error"
            data-error-kind={searchError.kind}
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-status-red flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-status-red">Search failed</div>
                <div className="text-[11px] text-red-200/80 mt-0.5">{searchError.message}</div>
              </div>
              <button
                onClick={() => searchMatters(searchQuery)}
                className="text-[11px] font-medium text-status-red hover:text-red-300 underline flex-shrink-0"
                data-testid="matter-search-retry"
              >
                Retry
              </button>
            </div>
          </div>
        )}
        {searchResults.length > 0 && (
          <div className="mt-1 border border-gray-700 rounded-lg bg-gray-800/80 max-h-48 overflow-y-auto" data-testid="matter-search-results">
            {searchResults.map((m, i) => {
              const name = m.matter_title || m.title || m.reference || m.client_name || 'Unknown';
              const ref = m.reference || '';
              const client = m.client_name || '';
              return (
                <div key={i} onClick={() => selectMatter(m)}
                  className="px-3 py-2 hover:bg-indigo-900/40 cursor-pointer border-b border-gray-700/50 last:border-0 transition-colors"
                  data-testid={`matter-result-${i}`}>
                  <div className="text-xs text-white font-medium truncate">{name}</div>
                  {client && client !== name && <div className="text-[10px] text-gray-500 truncate">{client}</div>}
                  {ref && ref !== name && <div className="text-[10px] text-gray-600 truncate">{ref}</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* SUBFOLDER NAVIGATION */}
      {subfolders.length > 0 && selectedFolder && (
        <div className="border border-gray-700/50 rounded-lg bg-gray-800/30 p-2" data-testid="subfolder-nav">
          <div className="flex items-center gap-2 mb-1.5">
            {subfolderStack.length > 0 && (
              <button onClick={goBackSubfolder} className="text-blue-400 hover:text-blue-300 p-0.5" data-testid="subfolder-back-btn">
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <span className="text-xs font-medium text-gray-300 truncate">{selectedFolder.name || selectedFolder.reference}</span>
          </div>
          {loadingSubfolders ? (
            <div className="flex items-center gap-2 py-2"><Loader2 className="w-4 h-4 text-blue-400 animate-spin" /><span className="text-xs text-gray-500">Loading...</span></div>
          ) : (
            <div className="space-y-0.5">
              {subfolders.map((sub, i) => (
                <div key={i} onClick={() => selectSubfolder(sub)}
                  className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-gray-700/50 transition-colors"
                  data-testid={`subfolder-${i}`}>
                  <Folder className="w-3.5 h-3.5 text-status-amber/70" />
                  <span className="text-xs text-gray-300 truncate flex-1">{sub.name}</span>
                  <ChevronRight className="w-3 h-3 text-gray-600" />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* SELECTED FOLDER + FILE BUTTON */}
      {selectedFolder && (
        <div className="space-y-2" data-testid="selected-folder-section">
          <div className="flex items-center gap-2 p-2.5 rounded-lg border border-teal/30 bg-teal-dim/10">
            <FolderOpen className="w-4 h-4 text-teal flex-shrink-0" />
            <span className="text-xs text-teal font-medium truncate flex-1" data-testid="selected-folder-path">
              {selectedFolder.name || selectedFolder.reference || selectedFolder.sharePointUrl || 'Selected folder'}
            </span>
            <button onClick={addToFavorites} className="text-gray-500 hover:text-status-amber p-0.5" title="Add to favorites" data-testid="add-favorite-btn">
              <Star className="w-3.5 h-3.5" />
            </button>
            <button onClick={clearSelection} className="text-gray-500 hover:text-status-red p-0.5" title="Clear" data-testid="clear-folder-btn">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {currentEmail?.hasAttachments && (
            <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer" data-testid="include-attachments-label">
              <input type="checkbox" checked={includeAttachments} onChange={e => setIncludeAttachments(e.target.checked)}
                className="rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500 w-3.5 h-3.5"
                data-testid="include-attachments" />
              <Paperclip className="w-3 h-3" />
              Include attachments
            </label>
          )}
          {includeAttachments && subfolders.length > 0 && (
            <div className="ml-5 mt-1">
              <label className="text-[10px] text-gray-500 block mb-1">Attachments destination</label>
              <select
                value={attachmentFolderId || selectedFolder?.itemId || ''}
                onChange={e => {
                  const sub = subfolders.find(s => s.id === e.target.value);
                  setAttachmentFolderId(e.target.value);
                  setAttachmentFolderName(sub?.name || '');
                }}
                className="w-full text-xs bg-gray-800 border border-gray-700 rounded px-2 py-1 text-gray-300"
                data-testid="attachment-folder-select"
              >
                <option value={selectedFolder?.itemId || ''}>Same as email ({selectedFolder?.name?.split(' / ').pop() || 'selected folder'})</option>
                {subfolders.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Mail PR A FE — "+ Also file to..." control.  Opens the
              <AdditionalDestinationsPicker /> to let the user multi-select
              matter top-level folders that the filed .eml will be copied
              into AFTER the canonical Attendances save.  Zero-selection
              is a valid outcome. */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="also-file-to-row">
            <button
              type="button"
              onClick={() => {
                if (!selectedFolder?.driveId) {
                  toast.error('Select a matter first');
                  return;
                }
                setDestPickerOpen(true);
              }}
              disabled={filingInProgress || !selectedFolder?.driveId}
              className="text-xs text-teal hover:text-teal underline disabled:opacity-50"
              data-testid="file-tab-also-file-to-button"
            >
              + Also file to...
            </button>
            {additionalDestinations.length > 0 && (
              <span
                className="text-[11px] text-teal"
                data-testid="file-tab-additional-destinations-count"
              >
                {additionalDestinations.length} extra {additionalDestinations.length === 1 ? 'copy' : 'copies'} selected
              </span>
            )}
          </div>

          <div className={`grid gap-2 ${timeRecordingEnabled ? 'grid-cols-2' : 'grid-cols-1'}`} data-testid="action-buttons-grid">
            <Button onClick={handleFile} disabled={filingInProgress || !currentEmail?.id}
              className="w-full bg-teal hover:bg-teal text-white h-9"
              data-testid="file-btn">
              {filingInProgress ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Filing...</> : <><Upload className="w-4 h-4 mr-1.5" />File Email</>}
            </Button>
            {timeRecordingEnabled && (
              <Button onClick={() => onOpenTimePanel?.()} variant="outline"
                className="w-full border-indigo-700 text-indigo-300 hover:bg-indigo-900/30 h-9"
                data-testid="time-btn">
                <Clock className="w-4 h-4 mr-1.5" />Record Time
              </Button>
            )}
          </div>
        </div>
      )}

      {/* DROP ZONE */}
      {selectedFolder && (
        <div
          className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${dropActive ? 'border-teal bg-teal-dim/20' : 'border-gray-700/50 bg-gray-800/20'}`}
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDropActive(true); }}
          onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDropActive(false); }}
          onDrop={handleDrop}
          data-testid="drop-zone">
          {dropActive ? (
            <div className="text-teal text-xs font-medium" data-testid="drop-zone-active">Drop to upload</div>
          ) : (
            <div className="text-xs text-gray-500" data-testid="drop-zone-idle">
              <Upload className="w-5 h-5 mx-auto mb-1 text-gray-600" />
              Drag files here to upload to <strong className="text-teal">{(selectedFolder.reference || selectedFolder.name || 'selected folder').split(' → ').pop().trim()}</strong>
            </div>
          )}
        </div>
      )}

      {/* FILING RESULT */}
      {filingResult && (
        <div className={`p-3 rounded-lg border ${filingResult.success ? 'bg-status-green-dim/20 border-green-800' : 'bg-status-red-dim/20 border-red-800'}`} data-testid="filing-result">
          <div className="flex items-center gap-2">
            {filingResult.success ? <Check className="w-4 h-4 text-status-green" /> : <AlertCircle className="w-4 h-4 text-status-red" />}
            <span className={`text-sm font-medium ${filingResult.success ? 'text-status-green' : 'text-status-red'}`}>{filingResult.message}</span>
          </div>
          {filingResult.url && (
            <a href={filingResult.url} target="_blank" rel="noreferrer" className="text-xs text-blue-400 hover:underline block mt-1">View in SharePoint</a>
          )}
          {/* Mail PR A FE — per-folder copy outcomes.  Renders ✓/⚠/✗ rows
              for each entry in response.additional_copies.  If ANY copy
              failed, the panel surfaces the failure visibly (the wrapper
              colour above already reflects this through filingResult.success). */}
          {Array.isArray(filingResult.additionalCopies) && filingResult.additionalCopies.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-700" data-testid="file-tab-filing-confirmation">
              <AdditionalCopiesConfirmation
                canonicalLabel={(selectedFolder?.name || 'Attendances').split(' / ').pop()}
                additionalCopies={filingResult.additionalCopies}
                compact
              />
            </div>
          )}
          {/* Undo button — shown for both auto-filed and manually filed emails */}
          {filingResult.success && filingResult.filingId && (
            <div className="mt-2 pt-2 border-t border-gray-700">
              <button onClick={() => undoFiling(filingResult.filingId)} disabled={undoingFiling}
                className="flex items-center gap-1.5 text-xs text-status-amber hover:text-status-amber transition-colors disabled:opacity-50"
                data-testid="undo-filing-btn">
                {undoingFiling ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                {undoingFiling ? 'Undoing...' : 'Undo filing'}
              </button>
            </div>
          )}
          {filingResult.success && currentEmail?.id && !autoDeleteAfterFiling && (
            <div className="mt-2 pt-2 border-t border-gray-700">
              <p className="text-xs text-gray-400 mb-1.5">What would you like to do with this email?</p>
              <div className="flex gap-2">
                <button onClick={() => deleteFiledEmail(currentEmail.id)}
                  className="flex-1 text-xs py-1.5 px-2 rounded bg-status-red-dim/40 border border-red-800 text-status-red hover:bg-status-red-dim/60 transition-colors"
                  data-testid="delete-after-filing-btn">Delete email</button>
                <button onClick={() => setFilingResult(prev => ({ ...prev, dismissed: true }))}
                  className="flex-1 text-xs py-1.5 px-2 rounded bg-gray-700/50 border border-gray-600 text-gray-300 hover:bg-gray-700 transition-colors"
                  data-testid="keep-after-filing-btn">Keep in inbox</button>
              </div>
            </div>
          )}
          {filingResult.success && autoDeleteAfterFiling && (
            <div className="mt-2 pt-2 border-t border-gray-700">
              <p className="text-xs text-status-amber">Auto-deleting email...</p>
            </div>
          )}
          {filingResult.success && currentEmail?.conversationId && !conversationBatch && (
            <div className="mt-2 pt-2 border-t border-gray-700">
              <p className="text-xs text-gray-400 mb-1.5">This email is part of a conversation.</p>
              <button onClick={fileConversation}
                className="text-xs text-blue-400 hover:text-blue-300 underline"
                data-testid="file-conversation-btn">File entire conversation</button>
            </div>
          )}
        </div>
      )}

      {/* CONVERSATION BATCH PROGRESS */}
      {conversationBatch && (
        <div className="p-3 rounded-lg border bg-gray-800 border-gray-700" data-testid="conversation-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-white">Filing conversation ({conversationBatch.total} messages)</span>
            <span className={`text-xs ${conversationBatch.status === 'completed' ? (conversationBatch.failed > 0 ? 'text-yellow-400' : 'text-status-green') : 'text-blue-400'}`}>
              {conversationBatch.status === 'completed' ? (conversationBatch.failed > 0 ? `${conversationBatch.completed} filed, ${conversationBatch.failed} failed` : 'Complete!') : 'Processing...'}
            </span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2 mb-1">
            <div className={`h-2 rounded-full transition-all ${conversationBatch.status === 'completed' ? (conversationBatch.failed > 0 ? 'bg-yellow-500' : 'bg-status-green') : 'bg-blue-500'}`}
              style={{ width: `${Math.round(((conversationBatch.completed + conversationBatch.failed) / conversationBatch.total) * 100)}%` }} />
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {conversationBatch.completed} of {conversationBatch.total} filed
            {conversationBatch.failed > 0 && `, ${conversationBatch.failed} failed`}
          </div>
        </div>
      )}

      {/* FILING HISTORY */}
      <div data-testid="recent-filings-section">
        <button onClick={() => setRecentFilingsExpanded(v => !v)}
          className="flex items-center gap-1.5 w-full text-left text-xs font-semibold text-gray-500 hover:text-gray-400 transition-colors"
          data-testid="recent-filings-toggle">
          <FileText className="w-3 h-3" />
          Recently filed ({recentFilings.length})
          <ChevronDown className={`w-3 h-3 ml-auto transition-transform ${recentFilingsExpanded ? 'rotate-180' : ''}`} />
        </button>
        {recentFilingsExpanded && (
          <div className="mt-1.5 space-y-1" data-testid="recent-filings-list">
            {recentFilings.length === 0 ? (
              <div className="text-xs text-gray-600 py-1">No recent filings</div>
            ) : recentFilings.map((f, i) => {
              let ref = f.matter_reference || '';
              if (!ref && f.sharepoint_path) {
                const parts = f.sharepoint_path.replace(/\/+$/, '').split('/').filter(Boolean);
                const slIdx = parts.findIndex(p => p.toUpperCase() === 'SL');
                ref = (slIdx >= 0 && slIdx + 1 < parts.length) ? parts[slIdx + 1] : (parts[parts.length - 1] || '');
                try { ref = decodeURIComponent(ref); } catch {}
              }
              ref = ref || 'Filed';
              const subj = (f.subject || '').substring(0, 35);
              const date = f.filed_at ? new Date(f.filed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '';
              return (
                <div key={i} className="flex items-center gap-2 p-2 rounded border border-teal/30 bg-teal-dim/10 hover:bg-teal-dim/20 transition-colors" data-testid={`recent-filing-${i}`}>
                  <Check className="w-3.5 h-3.5 text-teal flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-teal font-medium">{ref}</span>
                    <span className="text-xs text-gray-500 ml-1 truncate">{subj}</span>
                  </div>
                  <span className="text-xs text-gray-600 flex-shrink-0">{date}</span>
                  {(f.sharepoint_url || f.emailFileUrl) && (
                    <a href={f.sharepoint_url || f.emailFileUrl} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className="text-blue-400 hover:text-blue-300 p-0.5">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* MORNING DIGEST */}
      {morningDigest && (
        <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-3" data-testid="morning-digest">
          <div className="flex items-center gap-2 mb-2">
            <Sun className="w-4 h-4 text-status-amber" />
            <span className="text-xs font-bold text-status-amber">Morning filing digest</span>
            <span className="text-[9px] text-slate-500 ml-auto">Last 7 days</span>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <div className="bg-status-amber-dim/30 rounded px-2 py-1 text-center">
              <div className="text-sm font-bold text-status-amber">{morningDigest.unfiled_count || 0}</div>
              <div className="text-[9px] text-status-amber">Unfiled</div>
            </div>
            <div className="bg-blue-900/30 rounded px-2 py-1 text-center">
              <div className="text-sm font-bold text-blue-400">{morningDigest.likely_client_related || 0}</div>
              <div className="text-[9px] text-blue-300">Client-related</div>
            </div>
            {morningDigest.courts_authorities > 0 && (
              <div className="bg-status-red-dim/30 rounded px-2 py-1 text-center">
                <div className="text-sm font-bold text-status-red">{morningDigest.courts_authorities}</div>
                <div className="text-[9px] text-status-red">Courts</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mail PR A FE — Additional destinations picker.  Reuses the
          subfolder fetch (same endpoint <AttachmentSavePrompt /> uses)
          via loadPromptFolders. */}
      <AdditionalDestinationsPicker
        isOpen={destPickerOpen}
        onClose={() => setDestPickerOpen(false)}
        matterRef={selectedFolder?.reference || ''}
        currentSelection={additionalDestinations}
        folders={promptFolders}
        loadingFolders={promptFoldersLoading}
        foldersError={promptFoldersError}
        onLoadFolders={() => loadPromptFolders(
          selectedFolder?.driveId,
          // Prefer the matter root (parent) folder id over the
          // Attendances subfolder so the picker shows top-level matter
          // folders.  selectedFolder._parentFolderId is set in
          // loadSubfolders when an attendances child is auto-selected.
          selectedFolder?._parentFolderId || selectedFolder?.itemId
        )}
        onConfirm={(ids) => setAdditionalDestinations(ids)}
      />

      {/* Mail PR B — per-email attachment-save prompt. */}
      <AttachmentSavePrompt
        open={attachmentPromptOpen}
        onOpenChange={(o) => {
          if (!o) {
            setAttachmentPromptOpen(false);
            if (attachmentPromptCtx?.resolve && !attachmentPromptCtx.resolve.__handled) {
              attachmentPromptCtx.resolve({
                include_attachments: false,
                attachment_folder_id: null,
                attachment_decision: 'skipped',
              });
            }
            setAttachmentPromptCtx(null);
          }
        }}
        attachmentCount={attachmentPromptCtx?.attachmentCount || 0}
        emailCount={1}
        mode="single"
        defaultFolderName="Documents"
        folders={promptFolders}
        loadingFolders={promptFoldersLoading}
        foldersError={promptFoldersError}
        onLoadFolders={() => loadPromptFolders(
          attachmentPromptCtx?.folderDriveId,
          attachmentPromptCtx?.folderItemId
        )}
        onDecision={(decision) => {
          if (attachmentPromptCtx?.resolve) {
            attachmentPromptCtx.resolve(decision);
            attachmentPromptCtx.resolve.__handled = true;
          }
          setAttachmentPromptOpen(false);
          setAttachmentPromptCtx(null);
        }}
      />
    </div>
  );
}
